import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import { createPortal }          from 'react-dom';
import { useEffect }             from 'react';
import { observer }              from 'mobx-react-lite';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import buildingPickStore         from '@/TokamapEditor/stores/buildingPickStore';
import Kbd                       from '@/TokamapEditor/ux/Kbd';
import MagicWandOutlined         from '@/TokamapEditor/ux/icons/MagicWandOutlined';
import useDesignTokens           from '@/hooks/useDesignTokens';
import { MAX_SELECTION_SHAPES }  from '@/TokamapEditor/ux/constants';

const BuildingPickBar = observer(() => {
	const tokens = useDesignTokens();
	const container = currentEditorStore.get?.mapAdapter.getContainerElement();
	const active = buildingPickStore.active;
	const cursor = wandCursor({ wand: tokens.text.default, sparkle: tokens.color.primary, outline: tokens.surface.card });

	useEffect(() => {
		if (!active || !container) return;

		container.style.setProperty(CURSOR_PROPERTY, cursor);

		return () => void container.style.removeProperty(CURSOR_PROPERTY);
	}, [active, container, cursor]);

	if (!active || !container) return null;

	const { count, zoneResultCount } = buildingPickStore;
	const shown = zoneResultCount ?? count;
	const overLimit = shown > MAX_SELECTION_SHAPES;

	return createPortal(
		<View abs absB={16} absL={0} w="100%" flex center zIndex={10} style={{ pointerEvents: 'none', userSelect: 'none' }}>
			<View
				row
				centerV
				gap="md"
				pl={16}
				pr={8}
				py={8}
				br={999}
				bg={tokens.surface.elevated}
				size={13}
				style={{ boxShadow: tokens.shadow.elevated, pointerEvents: 'auto' }}
			>
				<View row centerV gap="xs" bold color={overLimit ? tokens.color.error : undefined}>
					<MagicWandOutlined style={{ fontSize: 16, color: overLimit ? tokens.color.error : tokens.color.primary }} />
					{overLimit
						? `${shown} bâtiments · ${MAX_SELECTION_SHAPES} maximum`
						: shown
							? `${shown} bâtiment${shown > 1 ? 's' : ''} retenu${shown > 1 ? 's' : ''}`
							: 'Cliquez sur un bâtiment'}
				</View>

				<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
					<View row centerV gap="xs"><Kbd>Clic</Kbd>ajouter ou retirer</View>
					<View row centerV gap="xs"><Kbd>Ctrl</Kbd>+ glisser : sélection</View>
					<View row centerV gap="xs"><Kbd>Maj</Kbd>+ glisser : lasso</View>
				</View>

				<View row gap="xs">
					<Button onClick={() => buildingPickStore.stop()}>Annuler</Button>
					<Button type="primary" disabled={!count} onClick={() => void buildingPickStore.create()}>
						Créer
					</Button>
				</View>
			</View>
		</View>,
		container,
	);
});

export default BuildingPickBar;

const CURSOR_PROPERTY = '--building-pick-cursor';

/** Curseur en baguette, aux couleurs du thème, pointée en haut à gauche comme une flèche : le point actif est son bout. */
function wandCursor(colors: { wand: string; sparkle: string; outline: string }): string {
	const star = (x: number, y: number, r: number) => {
		const k = r * 0.28;
		return `M${x} ${y - r}L${x + k} ${y - k}L${x + r} ${y}L${x + k} ${y + k}L${x} ${y + r}L${x - k} ${y + k}L${x - r} ${y}L${x - k} ${y - k}Z`;
	};
	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">`
		+ `<line x1="27" y1="27" x2="5" y2="5" stroke="${colors.outline}" stroke-width="6" stroke-linecap="round"/>`
		+ `<line x1="27" y1="27" x2="11" y2="11" stroke="${colors.wand}" stroke-width="3.5" stroke-linecap="round"/>`
		+ `<line x1="11" y1="11" x2="5" y2="5" stroke="${colors.sparkle}" stroke-width="3.5" stroke-linecap="round"/>`
		+ `<path d="${star(19, 7, 5)}" fill="${colors.sparkle}" stroke="${colors.outline}" stroke-width="1.2" stroke-linejoin="round"/>`
		+ `<path d="${star(7, 19, 3.5)}" fill="${colors.sparkle}" stroke="${colors.outline}" stroke-width="1.2" stroke-linejoin="round"/>`
		+ `</svg>`;

	return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 5 5, crosshair`;
}
